import React, { Component } from "react";
import InActiveStateSVG from "../../Assets/svg/InActiveStateSVG.svg";
import ActiveStateSVG from "../../Assets/svg/ActiveStateSVG.svg";
import { Link } from "react-router-dom";
import { ChevronLeft } from "lucide-react";
import PropTypes from "prop-types";
import {
  FetcIndividualUserAction,
  grantAccessToServicesAction,
  FetchAllDepartmentsAction,
  AssignUserDepartmentsAction,
  RemoveUserDepartmentAction,
  FetchUserDepartmentsAction,
} from "../../Actions/AdminUser";
import { withRouterHooks } from "../../withRouters/withRoutersHook";
import { connect } from "react-redux";

class AdminUserPermissionChanger extends Component {
  static propTypes = {
    fetchUserSuccess: PropTypes.bool,
    loadingUser: PropTypes.bool,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    email: PropTypes.string,
    lastLogin: PropTypes.string,
    isBanned: PropTypes.bool,
    isAdmin: PropTypes.bool,
    isSuperuser: PropTypes.bool,
    isAppSuperuser: PropTypes.bool,
    isSubscribed: PropTypes.bool,
    hasAccessToSmartIQ: PropTypes.bool,
    hasAccessToSmartExtract: PropTypes.bool,
    hasAccessToAI: PropTypes.bool,
    hasAccessToMessaging: PropTypes.bool,
    departments: PropTypes.array,
    userActiveDepartments: PropTypes.array,
    userDepartmentsList: PropTypes.array,
    FetcIndividualUserAction: PropTypes.func.isRequired,
    grantAccessToServicesAction: PropTypes.func.isRequired,
    FetchAllDepartmentsAction: PropTypes.func.isRequired,
    AssignUserDepartmentsAction: PropTypes.func.isRequired,
    RemoveUserDepartmentAction: PropTypes.func.isRequired,
    FetchUserDepartmentsAction: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.state = {
      isSmartIQActive: props.hasAccessToSmartIQ || false,
      isSmartExtractActive: props.hasAccessToSmartExtract || false,
      isCX2BOTActive: props.hasAccessToAI || false,
      isMessagingActive: props.hasAccessToMessaging || false,
      isSubscribed: props.isSubscribed || false,
      // Department management state
      selectedDepartments: [],
      showDepartmentDropdown: false,
      departmentLoading: false,
      departmentError: null,
      departmentSuccess: null,
    };
  }

  componentDidMount() {
    const email = this.extractEmailFromPath();
    this.props.FetcIndividualUserAction(email);
    this.props.FetchAllDepartmentsAction();
    this.props.FetchUserDepartmentsAction(email);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.hasAccessToSmartIQ !== this.props.hasAccessToSmartIQ) {
      this.setState({
        isSmartIQActive: this.props.hasAccessToSmartIQ,
      });
    }
    if (
      prevProps.hasAccessToSmartExtract !== this.props.hasAccessToSmartExtract
    ) {
      this.setState({
        isSmartExtractActive: this.props.hasAccessToSmartExtract,
      });
    }
    if (prevProps.hasAccessToAI !== this.props.hasAccessToAI) {
      this.setState({
        isCX2BOTActive: this.props.hasAccessToAI,
      });
    }
    if (prevProps.hasAccessToMessaging !== this.props.hasAccessToMessaging) {
      this.setState({
        isMessagingActive: this.props.hasAccessToMessaging,
      });
    }
    if (prevProps.isSubscribed !== this.props.isSubscribed) {
      this.setState({
        isSubscribed: this.props.isSubscribed,
      });
    }
  }

  extractEmailFromPath = () => {
    const path = this.props.location.pathname;
    const segments = path.split("/").filter(Boolean);
    return segments[segments.length - 1];
  };

  handleServiceToggle = (serviceKey, apiKey) => {
    const email = this.extractEmailFromPath();
    const newValue = !this.state[serviceKey];

    this.setState(
      {
        [serviceKey]: newValue,
      },
      () => {
        const data = {
          email: email,
          [apiKey]: newValue,
        };

        this.props
          .grantAccessToServicesAction(data)
          .then(() => {
            this.props.FetcIndividualUserAction(email);
          })
          .catch((error) => {
            console.error("Error updating service access:", error);
            // Optionally revert state if needed
            this.setState({
              [serviceKey]: !newValue,
            });
          });
      }
    );
  };

  // Department management methods
  handleDepartmentSelect = (departmentId) => {
    this.setState((prevState) => {
      const isSelected = prevState.selectedDepartments.includes(departmentId);
      return {
        selectedDepartments: isSelected
          ? prevState.selectedDepartments.filter((id) => id !== departmentId)
          : [...prevState.selectedDepartments, departmentId],
      };
    });
  };

  handleAssignDepartments = async () => {
    const { selectedDepartments } = this.state;
    const email = this.extractEmailFromPath();

    if (selectedDepartments.length === 0) {
      this.setState({ departmentError: "Please select at least one department" });
      return;
    }

    this.setState({ departmentLoading: true, departmentError: null, departmentSuccess: null });

    try {
      await this.props.AssignUserDepartmentsAction(email, selectedDepartments);
      this.setState({
        departmentLoading: false,
        departmentSuccess: "Departments assigned successfully",
        selectedDepartments: [],
        showDepartmentDropdown: false,
      });
      // Refresh user data
      this.props.FetcIndividualUserAction(email);
      this.props.FetchUserDepartmentsAction(email);

      // Clear success message after 3 seconds
      setTimeout(() => this.setState({ departmentSuccess: null }), 3000);
    } catch (error) {
      this.setState({
        departmentLoading: false,
        departmentError: error.response?.data?.error || "Failed to assign departments",
      });
      setTimeout(() => this.setState({ departmentError: null }), 5000);
    }
  };

  handleRemoveDepartment = async (departmentId) => {
    const email = this.extractEmailFromPath();

    this.setState({ departmentLoading: true, departmentError: null, departmentSuccess: null });

    try {
      await this.props.RemoveUserDepartmentAction(email, departmentId);
      this.setState({
        departmentLoading: false,
        departmentSuccess: "Department removed successfully",
      });
      // Refresh user data
      this.props.FetcIndividualUserAction(email);
      this.props.FetchUserDepartmentsAction(email);

      // Clear success message after 3 seconds
      setTimeout(() => this.setState({ departmentSuccess: null }), 3000);
    } catch (error) {
      this.setState({
        departmentLoading: false,
        departmentError: error.response?.data?.error || "Failed to remove department",
      });
      setTimeout(() => this.setState({ departmentError: null }), 5000);
    }
  };

  toggleDepartmentDropdown = () => {
    this.setState((prevState) => ({
      showDepartmentDropdown: !prevState.showDepartmentDropdown,
      selectedDepartments: [],
    }));
  };

  render() {
    const {
      fetchUserSuccess,
      firstName,
      lastName,
      email,
      lastLogin,
      isSuperuser,
      isAdmin,
    } = this.props;

    return (
      <div className="flex flex-col w-full min-h-0 flex-1 px-4 md:px-8 pt-6 md:pt-8 pb-16 gap-6">
        <div className="w-full flex flex-col gap-6">
          {/* Header — aligned with Admin & User Management */}
          <div className="flex flex-col gap-1">
            <div className="flex flex-wrap md:flex-nowrap items-center gap-3">
              <Link
                to="/admin-dashboard"
                className="p-1.5 rounded-md text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors shrink-0"
                aria-label="Back to admin"
              >
                <ChevronLeft className="w-5 h-5" />
              </Link>
              <h1 className="font-SFPro font-bold text-2xl text-gray-900">
                User Service Management
              </h1>
              <button
                type="button"
                onClick={() =>
                  this.handleServiceToggle("isSubscribed", "is_subscribed")
                }
                className={`ml-auto inline-flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-medium shadow-sm border transition-colors shrink-0 ${
                  this.state.isSubscribed
                    ? "bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100/80"
                    : "bg-white border-blue-100 text-gray-700 hover:bg-sky-50/80"
                }`}
              >
                <span>
                  {this.state.isSubscribed ? "Subscribed" : "Not subscribed"}
                </span>
                {this.state.isSubscribed ? (
                  <img className="w-5 h-5 shrink-0" src={ActiveStateSVG} alt="" />
                ) : (
                  <img className="w-5 h-5 shrink-0" src={InActiveStateSVG} alt="" />
                )}
              </button>
            </div>
            <p className="text-sm text-gray-600 pl-0 md:pl-11">
              Manage service for{" "}
              <span className="font-medium text-gray-900">
                {fetchUserSuccess
                  ? `${firstName || "NA"} ${lastName || "NA"}`
                  : "NA"}
              </span>
            </p>
          </div>

          {/* User Details */}
          <div className="flex flex-col bg-white border border-blue-100 rounded-xl shadow-sm overflow-hidden">
            <div
              className="px-6 py-4 border-b border-blue-100"
              style={{ background: "linear-gradient(135deg, #ffffff 0%, #eff6ff 100%)" }}
            >
              <p className="font-inter font-semibold text-sm text-gray-700 uppercase tracking-wider">
                User details
              </p>
            </div>
            <div className="px-6 py-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                <div className="flex flex-col gap-1.5 min-w-0">
                  <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Username
                  </div>
                  <div className="font-inter text-sm font-medium text-gray-900 truncate">
                    {fetchUserSuccess
                      ? `${firstName || "NA"} ${lastName || "NA"}`
                      : "NA"}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 min-w-0">
                  <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Email
                  </div>
                  <div className="font-inter text-sm font-medium text-gray-900 truncate">
                    {fetchUserSuccess ? `${email || "NA"}` : "NA"}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 min-w-0">
                  <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Role
                  </div>
                  <div className="font-inter text-sm font-medium text-gray-900">
                    {isSuperuser ? "Superuser" : isAdmin ? "Admin" : "User"}
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 min-w-0">
                  <div className="text-xs font-medium text-gray-400 uppercase tracking-wider">
                    Last active
                  </div>
                  <div className="font-inter text-sm font-medium text-gray-900">
                    {fetchUserSuccess && lastLogin
                      ? `${Math.floor(
                          (new Date() - new Date(lastLogin)) / (1000 * 60)
                        )} minutes ago`
                      : "NA"}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Department Access */}
          <div className="flex flex-col bg-white border border-blue-100 rounded-xl shadow-sm overflow-hidden">
            <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-blue-100 bg-gray-50/50">
              <p className="font-inter font-semibold text-sm text-gray-700 uppercase tracking-wider">
                Department access
              </p>
              <button
                type="button"
                onClick={this.toggleDepartmentDropdown}
                className="px-4 py-2 rounded-xl text-sm font-semibold text-white bg-primary-blue hover:bg-blue-700 transition-colors shadow-sm"
              >
                {this.state.showDepartmentDropdown ? "Cancel" : "+ Add"}
              </button>
            </div>

            <div className="px-6 py-5 flex flex-col gap-4">
            {this.state.departmentSuccess && (
              <div className="px-4 py-2.5 rounded-xl border border-green-200 bg-green-50 text-green-700 text-sm">
                {this.state.departmentSuccess}
              </div>
            )}
            {this.state.departmentError && (
              <div className="px-4 py-2.5 rounded-xl border border-red-200 bg-red-50 text-red-700 text-sm">
                {this.state.departmentError}
              </div>
            )}

            {this.state.showDepartmentDropdown && (
              <div className="flex flex-wrap items-center gap-2 p-3 bg-sky-50/40 rounded-xl border border-blue-100">
                {(this.props.departments || []).map((dept) => {
                  const isAlreadyAssigned = (this.props.userActiveDepartments || []).includes(dept.name);
                  const isSelected = this.state.selectedDepartments.includes(dept.id);
                  return (
                    <button
                      key={dept.id}
                      onClick={() => !isAlreadyAssigned && this.handleDepartmentSelect(dept.id)}
                      disabled={isAlreadyAssigned}
                      className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-all ${
                        isAlreadyAssigned
                          ? "bg-gray-100 text-gray-400 cursor-not-allowed line-through"
                          : isSelected
                          ? "bg-primary-blue text-white shadow-sm"
                          : "bg-white border border-blue-100 text-gray-600 hover:border-primary-blue/40 hover:text-primary-blue"
                      }`}
                    >
                      {dept.name}
                    </button>
                  );
                })}
                {this.state.selectedDepartments.length > 0 && (
                  <button
                    onClick={this.handleAssignDepartments}
                    disabled={this.state.departmentLoading}
                    className="ml-2 px-3 py-1.5 bg-emerald-600 text-white rounded-lg text-xs font-semibold hover:bg-emerald-700 transition-colors disabled:bg-gray-400 shadow-sm"
                  >
                    {this.state.departmentLoading ? "..." : `Save (${this.state.selectedDepartments.length})`}
                  </button>
                )}
              </div>
            )}

            {/* Current Departments - Inline */}
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-gray-500 font-medium">Assigned:</span>
              {(this.props.userActiveDepartments || []).length > 0 ? (
                (this.props.userDepartmentsList || [])
                  .filter(d => d.is_active)
                  .map((dept) => (
                    <div
                      key={dept.department_id}
                      className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-medium ring-1 ring-inset ring-blue-200/50"
                    >
                      <span>{dept.department_name}</span>
                      <button
                        onClick={() => this.handleRemoveDepartment(dept.department_id)}
                        disabled={this.state.departmentLoading}
                        className="w-5 h-5 flex items-center justify-center rounded-full hover:bg-red-500 hover:text-white transition-colors text-sm font-bold text-blue-400"
                        title="Remove"
                      >
                        ×
                      </button>
                    </div>
                  ))
              ) : (
                <span className="text-xs text-gray-400 italic">None</span>
              )}
            </div>
            </div>
          </div>

          {/* Services */}
          {this.props.isAppSuperuser && (
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-2">
              Service toggles are disabled for superusers. Superusers have full access to all services by default.
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            <button
              type="button"
              onClick={() => this.handleServiceToggle("isSmartIQActive", "has_access_to_smart_iq")}
              disabled={this.props.isAppSuperuser}
              title={this.props.isAppSuperuser ? "Cannot change services for a superuser" : undefined}
              className={`px-5 py-4 flex flex-col justify-between text-left rounded-xl border-2 transition-all shadow-sm ${
                this.props.isAppSuperuser
                  ? "opacity-60 cursor-not-allowed border-emerald-300 bg-emerald-50/70"
                  : this.state.isSmartIQActive
                  ? "border-emerald-300 bg-emerald-50/70 hover:shadow-md"
                  : "border-blue-100 bg-white hover:border-blue-200 hover:bg-sky-50/50 hover:shadow-md"
              }`}
            >
              <div className="flex justify-between items-start gap-2">
                <span className="font-SFPro font-semibold text-base text-gray-900">Survey IQ</span>
                <img className="w-6 h-6 shrink-0" src={this.state.isSmartIQActive ? ActiveStateSVG : InActiveStateSVG} alt="" />
              </div>
              <p className="text-sm text-gray-500 mt-2">Survey analytics</p>
              <div className="flex items-center gap-2 mt-3">
                <span className={`w-2.5 h-2.5 rounded-full ring-1 ring-inset ${this.state.isSmartIQActive ? "bg-emerald-500 ring-emerald-300/50" : "bg-gray-300 ring-gray-200"}`} />
                <span className="text-sm text-gray-600 font-medium">{this.state.isSmartIQActive ? "Active" : "Inactive"}</span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => this.handleServiceToggle("isSmartExtractActive", "has_access_to_smart_extract")}
              disabled={this.props.isAppSuperuser}
              title={this.props.isAppSuperuser ? "Cannot change services for a superuser" : undefined}
              className={`px-5 py-4 flex flex-col justify-between text-left rounded-xl border-2 transition-all shadow-sm ${
                this.props.isAppSuperuser
                  ? "opacity-60 cursor-not-allowed border-emerald-300 bg-emerald-50/70"
                  : this.state.isSmartExtractActive
                  ? "border-emerald-300 bg-emerald-50/70 hover:shadow-md"
                  : "border-blue-100 bg-white hover:border-blue-200 hover:bg-sky-50/50 hover:shadow-md"
              }`}
            >
              <div className="flex justify-between items-start gap-2">
                <span className="font-SFPro font-semibold text-base text-gray-900">SmartExtract</span>
                <img className="w-6 h-6 shrink-0" src={this.state.isSmartExtractActive ? ActiveStateSVG : InActiveStateSVG} alt="" />
              </div>
              <p className="text-sm text-gray-500 mt-2">PDF data analysis</p>
              <div className="flex items-center gap-2 mt-3">
                <span className={`w-2.5 h-2.5 rounded-full ring-1 ring-inset ${this.state.isSmartExtractActive ? "bg-emerald-500 ring-emerald-300/50" : "bg-gray-300 ring-gray-200"}`} />
                <span className="text-sm text-gray-600 font-medium">{this.state.isSmartExtractActive ? "Active" : "Inactive"}</span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => this.handleServiceToggle("isCX2BOTActive", "has_access_to_ai")}
              disabled={this.props.isAppSuperuser}
              title={this.props.isAppSuperuser ? "Cannot change services for a superuser" : undefined}
              className={`px-5 py-4 flex flex-col justify-between text-left rounded-xl border-2 transition-all shadow-sm ${
                this.props.isAppSuperuser
                  ? "opacity-60 cursor-not-allowed border-emerald-300 bg-emerald-50/70"
                  : this.state.isCX2BOTActive
                  ? "border-emerald-300 bg-emerald-50/70 hover:shadow-md"
                  : "border-blue-100 bg-white hover:border-blue-200 hover:bg-sky-50/50 hover:shadow-md"
              }`}
            >
              <div className="flex justify-between items-start gap-2">
                <span className="font-SFPro font-semibold text-base text-gray-900">CX2BOT</span>
                <img className="w-6 h-6 shrink-0" src={this.state.isCX2BOTActive ? ActiveStateSVG : InActiveStateSVG} alt="" />
              </div>
              <p className="text-sm text-gray-500 mt-2">AI assistant</p>
              <div className="flex items-center gap-2 mt-3">
                <span className={`w-2.5 h-2.5 rounded-full ring-1 ring-inset ${this.state.isCX2BOTActive ? "bg-emerald-500 ring-emerald-300/50" : "bg-gray-300 ring-gray-200"}`} />
                <span className="text-sm text-gray-600 font-medium">{this.state.isCX2BOTActive ? "Active" : "Inactive"}</span>
              </div>
            </button>

            <button
              type="button"
              onClick={() => this.handleServiceToggle("isMessagingActive", "has_access_to_messaging")}
              disabled={this.props.isAppSuperuser}
              title={this.props.isAppSuperuser ? "Cannot change services for a superuser" : undefined}
              className={`px-5 py-4 flex flex-col justify-between text-left rounded-xl border-2 transition-all shadow-sm ${
                this.props.isAppSuperuser
                  ? "opacity-60 cursor-not-allowed border-emerald-300 bg-emerald-50/70"
                  : this.state.isMessagingActive
                  ? "border-emerald-300 bg-emerald-50/70 hover:shadow-md"
                  : "border-blue-100 bg-white hover:border-blue-200 hover:bg-sky-50/50 hover:shadow-md"
              }`}
            >
              <div className="flex justify-between items-start gap-2">
                <span className="font-SFPro font-semibold text-base text-gray-900">Messaging</span>
                <img className="w-6 h-6 shrink-0" src={this.state.isMessagingActive ? ActiveStateSVG : InActiveStateSVG} alt="" />
              </div>
              <p className="text-sm text-gray-500 mt-2">Message management</p>
              <div className="flex items-center gap-2 mt-3">
                <span className={`w-2.5 h-2.5 rounded-full ring-1 ring-inset ${this.state.isMessagingActive ? "bg-emerald-500 ring-emerald-300/50" : "bg-gray-300 ring-gray-200"}`} />
                <span className="text-sm text-gray-600 font-medium">{this.state.isMessagingActive ? "Active" : "Inactive"}</span>
              </div>
            </button>
          </div>
        </div>
      </div>
    );
  }
}

const mapStateToProps = (state) => ({
  fetchUserSuccess: state.adminUser.fetchUserSuccess,
  loadingUser: state.adminUser.loadingUser,
  firstName: state.adminUser.firstName,
  lastName: state.adminUser.lastName,
  email: state.adminUser.email,
  lastLogin: state.adminUser.lastLogin,
  isBanned: state.adminUser.isBanned,
  isAdmin: state.adminUser.isAdmin,
  isSuperuser: state.adminUser.isSuperuser,
  isAppSuperuser: state.adminUser.isAppSuperuser,
  isSubscribed: state.adminUser.isSubscribed,
  hasAccessToSmartIQ: state.adminUser.hasAccessToSmartIQ,
  hasAccessToSmartExtract: state.adminUser.hasAccessToSmartExtract,
  hasAccessToAI: state.adminUser.hasAccessToAI,
  hasAccessToMessaging: state.adminUser.hasAccessToMessaging,
  departments: state.adminUser.departments,
  userActiveDepartments: state.adminUser.userActiveDepartments,
  userDepartmentsList: state.adminUser.userDepartmentsList,
});

export default withRouterHooks(
  connect(mapStateToProps, {
    FetcIndividualUserAction,
    grantAccessToServicesAction,
    FetchAllDepartmentsAction,
    AssignUserDepartmentsAction,
    RemoveUserDepartmentAction,
    FetchUserDepartmentsAction,
  })(AdminUserPermissionChanger)
);
